/**
 * FreedrIQGrowth — /freedriq/growth/:tier
 *
 * Growth report page comparing the user's pre and post FreedrIQ for a tier.
 * Shows: score delta, confidence delta, category-by-category improvement,
 * and a shareable "growth card" PNG.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import { Button } from '../components/ui/button';
import AskExpertButton from '../components/AskExpertButton';
import {
  Sparkles, TrendingUp, Award, ArrowLeft, ArrowUp, ArrowDown, Loader2,
  Download, ClipboardCopy, GraduationCap,
} from 'lucide-react';

const FreedrIQGrowth = () => {
  const { API, user } = useContext(AuthContext);
  const { tier = 'free' } = useParams();
  const [loading, setLoading] = useState(true);
  const [pre, setPre] = useState(null);
  const [post, setPost] = useState(null);

  useEffect(() => {
    if (!user) return;
    // /freedriq/growth just needs summary + a targeted lookup. We reuse the
    // by-tier data returned by /summary and fetch full docs via existing helper.
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/freedriq/growth?tier=${tier}`);
        setPre(data.pre);
        setPost(data.post);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load growth report');
      } finally { setLoading(false); }
    };
    load();
  }, [API, user, tier]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <ToolSelector />
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading growth report…
        </div>
      </div>
    );
  }

  if (!pre || !post) {
    return (
      <div className="min-h-screen bg-background">
        <ToolSelector />
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-6">
          <div className="pro-card p-8 text-center" data-testid="growth-empty">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 text-primary mb-4">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold">
              You haven&apos;t completed both attempts yet
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              A growth report needs both a `pre` and a `post` FreedrIQ for the {tier} tier.
              {!pre && ' Take the entry assessment first.'}
              {pre && !post && ' Take the post assessment to unlock the growth report.'}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {!pre && (
                <Link to={`/freedriq?tier=${tier}&phase=pre`} className="btn-primary rounded-full font-bold text-xs px-4 py-2" data-testid="growth-take-pre">
                  Take entry FreedrIQ <Sparkles className="inline w-3 h-3 ml-1.5" />
                </Link>
              )}
              {pre && !post && (
                <Link to={`/freedriq?tier=${tier}&phase=post`} className="btn-primary rounded-full font-bold text-xs px-4 py-2" data-testid="growth-take-post">
                  Take post FreedrIQ <Sparkles className="inline w-3 h-3 ml-1.5" />
                </Link>
              )}
              <Link to="/dashboard" className="text-xs text-muted-foreground hover:underline">Back to Dashboard</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const preScore  = pre.score || 0;
  const postScore = post.score || 0;
  const delta     = postScore - preScore;
  const preConf   = (pre.confidence || {}).score  || 0;
  const postConf  = (post.confidence || {}).score || 0;
  const confDelta = postConf - preConf;
  const shareUrl  = `${API}/freedriq/growth-card/${tier}.png`;

  // Category-by-category deltas.
  const preCats  = (pre.technical  || {}).category_breakdown || {};
  const postCats = (post.technical || {}).category_breakdown || {};
  const allCats  = Array.from(new Set([...Object.keys(preCats), ...Object.keys(postCats)]));
  const catRows  = allCats.map((cat) => {
    const p = preCats[cat]?.percent ?? 0;
    const q = postCats[cat]?.percent ?? 0;
    return { cat, pre: p, post: q, delta: q - p };
  }).sort((a, b) => b.delta - a.delta);

  return (
    <div className="min-h-screen bg-background" data-testid="freedriq-growth-page">
      <ToolSelector />
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <Link to="/dashboard" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </Link>
          <div className="eyebrow text-primary inline-flex items-center gap-1.5 mt-1">
            <TrendingUp className="w-3 h-3" /> FreedrIQ · {tier} · Growth Report
          </div>
          <h1 className="font-heading text-4xl font-extrabold mt-1">
            You gained <span className={delta >= 0 ? 'text-primary' : 'text-red-600'}>{delta >= 0 ? '+' : ''}{delta}</span> points
          </h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            A side-by-side of your entry and post-learning FreedrIQ across every category.
          </p>
          <div className="mt-3">
            <AskExpertButton
              variant="chip"
              label="Explain this report"
              context={{
                type:  'freedriq_report',
                id:    `growth-${tier}`,
                title: `FreedrIQ ${tier} growth report`,
                snippet:
                  `Tier: ${tier}. Pre-score ${preScore}, post-score ${postScore} (delta ${delta}). ` +
                  `Pre-confidence ${preConf}, post-confidence ${postConf} (delta ${confDelta}). ` +
                  `Category deltas: ${catRows.map(r => `${r.cat} ${r.pre}%→${r.post}%`).join('; ')}.`,
              }}
            />
          </div>
        </div>

        {/* Deltas */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="pro-card p-5" data-testid="growth-score-delta">
            <div className="eyebrow text-primary inline-flex items-center gap-1.5">
              <Award className="w-3 h-3" /> FreezeDryIQ Score
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="font-heading text-3xl font-extrabold">{preScore}</div>
              <ArrowRightArrow />
              <div className="font-heading text-3xl font-extrabold text-primary">{postScore}</div>
            </div>
            <DeltaChip value={delta} />
          </div>
          <div className="pro-card p-5" data-testid="growth-confidence-delta">
            <div className="eyebrow text-primary inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Confidence baseline
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="font-heading text-3xl font-extrabold">{preConf}</div>
              <ArrowRightArrow />
              <div className="font-heading text-3xl font-extrabold text-primary">{postConf}</div>
            </div>
            <DeltaChip value={confDelta} />
          </div>
        </div>

        {/* Category breakdown */}
        {catRows.length > 0 && (
          <div className="pro-card p-6 mb-4" data-testid="growth-category-breakdown">
            <div className="eyebrow text-primary inline-flex items-center gap-1.5">
              <GraduationCap className="w-3 h-3" /> Growth by category
            </div>
            <div className="space-y-3 mt-3">
              {catRows.map((row) => (
                <div key={row.cat} data-testid={`growth-cat-${row.cat.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-bold truncate mr-2">{row.cat}</span>
                    <span className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-muted-foreground">{row.pre}%</span>
                      <ArrowRightArrow />
                      <span className="font-heading font-extrabold">{row.post}%</span>
                      <DeltaChip value={row.delta} tiny />
                    </span>
                  </div>
                  <div className="relative h-2 rounded-full bg-muted overflow-hidden">
                    <div className="absolute top-0 left-0 h-full bg-muted-foreground/30" style={{ width: `${row.pre}%` }} />
                    <div className="absolute top-0 left-0 h-full bg-primary" style={{ width: `${row.post}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Share card */}
        <div className="pro-card p-4" data-testid="growth-share">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5 mb-2">
            <TrendingUp className="w-3 h-3" /> Share your growth
          </div>
          <img
            src={shareUrl}
            alt="Growth share card"
            className="w-full rounded-lg border border-border"
            loading="lazy"
          />
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <a
              href={shareUrl}
              download={`freezedryiq-growth-${tier}.png`}
              className="btn-primary inline-flex items-center gap-1.5 rounded-full font-bold text-xs px-3 py-2"
              data-testid="growth-share-download"
            >
              <Download className="w-3.5 h-3.5" /> Download PNG
            </a>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(shareUrl);
                toast.success('Share link copied');
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-border font-bold text-xs px-3 py-2 hover:bg-muted"
              data-testid="growth-share-copy"
            >
              <ClipboardCopy className="w-3.5 h-3.5" /> Copy image link
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <Link to="/dashboard" className="text-xs text-muted-foreground hover:text-foreground font-bold hover:underline">
            Back to Dashboard
          </Link>
          <Link to={`/freedriq?tier=${tier}&phase=post`} className="text-xs text-primary font-bold hover:underline inline-flex items-center gap-1">
            Retake post assessment <Sparkles className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const ArrowRightArrow = () => (
  <span className="text-muted-foreground text-xs">→</span>
);

const DeltaChip = ({ value, tiny = false }) => {
  const positive = value >= 0;
  const cls = positive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800';
  const Icon = positive ? ArrowUp : ArrowDown;
  const size = tiny ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5 mt-2';
  return (
    <span className={`inline-flex items-center gap-1 font-bold rounded-full ${cls} ${size}`}>
      <Icon className="w-3 h-3" /> {positive ? '+' : ''}{value}
    </span>
  );
};

export default FreedrIQGrowth;
